import type { Sentiment } from '@year13/kui/types/feedback';

import type { SubmitUserFeedbackInput } from '~/graphql/generated/graphql';

import { SUBMIT_USER_FEEDBACK } from '~/graphql/documents/userFeedback';
import { FeedbackSentiment, FeedbackSubjectType } from '~/graphql/generated/graphql';

const SENTIMENT_MAP: Record<Sentiment, FeedbackSentiment> = {
  POSITIVE: FeedbackSentiment.Positive,
  NEUTRAL: FeedbackSentiment.Neutral,
  NEGATIVE: FeedbackSentiment.Negative,
};

export const mapSentiment = (sentiment: Sentiment): FeedbackSentiment => SENTIMENT_MAP[sentiment];

const useSubmitFeedback = () => {
  const { $urqlClient, $logErrorMessage } = useNuxtApp();
  const submitting = ref(false);

  const submitFeedback = async (input: SubmitUserFeedbackInput) => {
    submitting.value = true;

    try {
      const { data, error } = await $urqlClient.mutation(SUBMIT_USER_FEEDBACK, {
        input,
      });

      if (error) {
        $logErrorMessage('Failed to submit feedback', { error });
        return undefined;
      }

      return data?.core?.userFeedback?.submitUserFeedback;
    }
    catch (err) {
      $logErrorMessage('Failed to submit feedback', { error: err });
      return undefined;
    }
    finally {
      submitting.value = false;
    }
  };

  return { submitFeedback, submitting: readonly(submitting) };
};

export const useJobFeedback = () => {
  const { submitFeedback, submitting } = useSubmitFeedback();

  const submitJobFeedback = async (
    jobId: string,
    sentiment: Sentiment,
    comment?: string,
  ) => {
    return await submitFeedback({
      subjectType: FeedbackSubjectType.Job,
      subjectId: jobId,
      sentiment: mapSentiment(sentiment),
      comment: comment || undefined,
    });
  };

  return { submitJobFeedback, submitting };
};

export const useInstitutionFeedback = () => {
  const { submitFeedback, submitting } = useSubmitFeedback();

  const submitInstitutionFeedback = async (
    institutionId: string,
    sentiment: Sentiment,
    comment?: string,
  ) => {
    return await submitFeedback({
      subjectType: FeedbackSubjectType.Institution,
      subjectId: institutionId,
      sentiment: mapSentiment(sentiment),
      comment: comment || undefined,
    });
  };

  return { submitInstitutionFeedback, submitting };
};

export const useLearningAssetFeedback = () => {
  const { submitFeedback, submitting } = useSubmitFeedback();

  const submitLearningAssetFeedback = async (
    learningAssetId: string,
    sentiment: Sentiment,
    metadata?: Record<string, string>,
  ) => {
    return await submitFeedback({
      subjectType: FeedbackSubjectType.LearningAsset,
      subjectId: learningAssetId,
      sentiment: mapSentiment(sentiment),
      metadata: metadata ? JSON.stringify(metadata) : undefined,
    });
  };

  return { submitLearningAssetFeedback, submitting };
};

export const useGtkyQuizResultsFeedback = () => {
  const { submitFeedback, submitting } = useSubmitFeedback();

  const submitGtkyQuizResultsFeedback = async (
    sentiment: Sentiment,
    comment?: string,
  ) => {
    return await submitFeedback({
      subjectType: FeedbackSubjectType.GtkyQuizResults,
      sentiment: mapSentiment(sentiment),
      comment: comment || undefined,
    });
  };

  return { submitGtkyQuizResultsFeedback, submitting };
};
